import { SettingsManager } from "@viamap/viamap2-common";
import AWS from "aws-sdk";

type AuthStorageContext = {
   authData: { ClientId: string; Region?: string };
   idTokenKey: string;
   accessTokenKey: string;
   refreshTokenKey: string;
};

let refreshTokenPromise: Promise<string | undefined> | null = null;

function getAuthStorageContext(): AuthStorageContext | undefined {
   try {
      const authData = SettingsManager.getSystemSetting("authData") as { ClientId?: string; Region?: string } | undefined;
      if (!authData || !authData.ClientId) {
         return undefined;
      }

      const lastAuthUserKey = `CognitoIdentityServiceProvider.${authData.ClientId}.LastAuthUser`;
      const username = localStorage.getItem(lastAuthUserKey);

      if (!username) {
         return undefined;
      }

      return {
         authData: { ClientId: authData.ClientId, Region: authData.Region },
         idTokenKey: `CognitoIdentityServiceProvider.${authData.ClientId}.${username}.idToken`,
         accessTokenKey: `CognitoIdentityServiceProvider.${authData.ClientId}.${username}.accessToken`,
         refreshTokenKey: `CognitoIdentityServiceProvider.${authData.ClientId}.${username}.refreshToken`
      };
   } catch (error) {
      console.warn("Failed to read auth storage context:", error);
      return undefined;
   }
}

function getIdTokenFromStorage(): string | undefined {
   const context = getAuthStorageContext();
   if (!context) return undefined;
   try {
      return localStorage.getItem(context.idTokenKey) || undefined;
   } catch (error) {
      console.warn("Failed to get JWT token from localStorage:", error);
      return undefined;
   }
}

function decodeJwtPayload(token: string): { exp?: number } | undefined {
   try {
      const payload = token.split(".")[1];
      if (!payload) return undefined;
      const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
      const paddedPayload = normalizedPayload.padEnd(normalizedPayload.length + ((4 - normalizedPayload.length % 4) % 4), "=");
      return JSON.parse(atob(paddedPayload));
   } catch {
      return undefined;
   }
}

export function isTokenExpired(token: string, skewMs: number = 30 * 1000): boolean {
   const payload = decodeJwtPayload(token);
   if (!payload?.exp) {
      return false;
   }
   const expiresAtMs = payload.exp * 1000;
   return Date.now() >= (expiresAtMs - skewMs);
}

async function refreshIdToken(): Promise<string | undefined> {
   const context = getAuthStorageContext();
   if (!context?.authData.Region) {
      return undefined;
   }

   let refreshToken: string | null = null;
   try {
      refreshToken = localStorage.getItem(context.refreshTokenKey);
   } catch {
      refreshToken = null;
   }
   if (!refreshToken) {
      return undefined;
   }

   if (!refreshTokenPromise) {
      refreshTokenPromise = new Promise((resolve) => {
         const cognito = new AWS.CognitoIdentityServiceProvider({ region: context.authData.Region });
         cognito.initiateAuth(
            {
               AuthFlow: "REFRESH_TOKEN_AUTH",
               ClientId: context.authData.ClientId,
               AuthParameters: {
                  REFRESH_TOKEN: refreshToken || ""
               }
            } as AWS.CognitoIdentityServiceProvider.InitiateAuthRequest,
            (error, result) => {
               if (error) {
                  console.warn("Failed to refresh JWT token:", error);
                  resolve(undefined);
                  return;
               }

               const idToken = result?.AuthenticationResult?.IdToken;
               const accessToken = result?.AuthenticationResult?.AccessToken;
               if (!idToken) {
                  resolve(undefined);
                  return;
               }

               try {
                  localStorage.setItem(context.idTokenKey, idToken);
                  if (accessToken) {
                     localStorage.setItem(context.accessTokenKey, accessToken);
                  }
               } catch {
                  // Ignore storage write errors and still use the refreshed token in-memory.
               }
               resolve(idToken);
            }
         );
      });
   }

   try {
      return await refreshTokenPromise;
   } finally {
      refreshTokenPromise = null;
   }
}

/**
 * Cognito ID token for the logged-in user, refreshed when expired (or when forced).
 * Returns undefined when nobody is logged in, e.g. on a shared map.
 */
export async function getCognitoIdToken(options: { forceRefresh?: boolean; preferredToken?: string | null } = {}): Promise<string | undefined> {
   const token = options.preferredToken || getIdTokenFromStorage();
   if (token && !options.forceRefresh && !isTokenExpired(token)) {
      return token;
   }

   const refreshedToken = await refreshIdToken();
   return refreshedToken || token || undefined;
}
